import React, { useState } from "react";
import { X, Phone, MessageCircle, Send } from "lucide-react";
import { CONTACT } from "../data/mock";

export function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    area: "Gachibowli",
    service: "Complete Home Interior",
    budget: "₹3–5 Lakhs",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = `Hello Good Idea Interiors!

Name: ${form.name}
Phone: ${form.phone}
Email: ${form.email}
Area: ${form.area}, Hyderabad
Service: ${form.service}
Budget: ${form.budget}
Message: ${form.message || "I'd like a free consultation."}`;

    window.open(
      `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
    setSubmitted(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="contact-modal"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-[#1a1a1a] rounded-t-2xl p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-300 hover:text-white transition-colors"
            data-testid="modal-close-btn"
          >
            <X size={20} />
          </button>
          <h2 className="font-serif text-2xl font-bold">Get Free Quote</h2>
          <p className="text-gray-300 text-sm mt-1">Our Hyderabad team will call you within 30 minutes</p>

          <div className="flex gap-4 mt-4">
            <a
              href={`tel:${CONTACT.phone}`}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm transition-colors"
              data-testid="modal-call-link"
            >
              <Phone size={14} />
              Call Now
            </a>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(CONTACT.whatsappMsg)}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] px-4 py-2 rounded-full text-sm transition-colors"
              data-testid="modal-whatsapp-link"
            >
              <MessageCircle size={14} />
              WhatsApp
            </a>
          </div>
        </div>

        {/* Form */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[#C9A84C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✓</span>
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1a1a1a] mb-2">Thank You!</h3>
              <p className="text-gray-500 text-sm">Your query has been sent. Our team will contact you shortly.</p>
              <button
                onClick={onClose}
                className="mt-6 bg-[#C9A84C] text-white font-semibold px-8 py-3 rounded-full hover:bg-[#b8933e] transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                  <input
                    required
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]"
                    data-testid="modal-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone *</label>
                  <input
                    required
                    type="tel"
                    placeholder="Mobile number"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]"
                    data-testid="modal-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]"
                  data-testid="modal-email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Area in Hyderabad</label>
                  <select
                    value={form.area}
                    onChange={(e) => setForm({ ...form, area: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#C9A84C]"
                    data-testid="modal-area-select"
                  >
                    {["Gachibowli", "HITEC City", "Kondapur", "Madhapur", "Banjara Hills", "Jubilee Hills", "Kukatpally", "Kompally", "Miyapur", "LB Nagar", "Secunderabad", "Ameerpet", "Other"].map((a) => (
                      <option key={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Service</label>
                  <select
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#C9A84C]"
                    data-testid="modal-service-select"
                  >
                    {["Complete Home Interior", "Modular Kitchen", "Bedroom", "Living Room", "Wardrobe", "3D Design", "Other"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Budget</label>
                <select
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#C9A84C]"
                  data-testid="modal-budget-select"
                >
                  {["Under ₹3 Lakhs", "₹3–5 Lakhs", "₹5–8 Lakhs", "₹8–12 Lakhs", "Above ₹12 Lakhs"].map((b) => (
                    <option key={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Message (optional)</label>
                <textarea
                  rows={3}
                  placeholder="Tell us about your project..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none"
                  data-testid="modal-message-input"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#C9A84C] text-white font-bold py-3.5 rounded-lg hover:bg-[#b8933e] transition-colors flex items-center justify-center gap-2"
                data-testid="modal-submit-btn"
              >
                <Send size={16} />
                Send via WhatsApp
              </button>

              <p className="text-xs text-gray-400 text-center">
                Your details are secure and will only be used for consultation
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
